/**
 * QuoteWorkflowTutorial — 外销「筛选产品 → 自动生成报价单」交互式教程
 *
 * 2026-02 新增。用户希望有一个可视化的、逐步演示的教程，代替真实视频。
 * 组件设计：
 *   • 8 个步骤，垂直时间线布局，每步包含图标 / 标题 / 描述 / 关键提示 / 迷你 UI 示意图。
 *   • 每步右侧带"去这里操作"按钮，一键跳到系统对应页面，帮助用户边看边学。
 *   • 顶部有"整体流程概览"横向时间线，让用户对全流程有整体印象。
 *   • 中英双语通过 lang prop 切换，内容集中在 STEPS(lang) 中。
 *
 * 用于 HelpCenter 顶部大 CTA 卡片下方展开显示。
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Filter as FilterIcon,
  Search as SearchIcon,
  CheckSquare,
  ShoppingCart,
  FileText,
  Edit3,
  Download,
  ArrowRight,
  ChevronDown,
  Sparkles,
} from "lucide-react";

/* --------------------------------------------------------------------- */
/* 每个步骤的静态数据。放在函数里方便按语言切换。                             */
/* --------------------------------------------------------------------- */
const STEPS = (lang) => {
  const zh = lang === "zh";
  return [
    {
      id: "open-products",
      icon: Package,
      route: "/products",
      cta: zh ? "打开产品列表" : "Open Products",
      title: zh ? "进入产品列表" : "Open the Products list",
      desc: zh
        ? "左侧菜单点击「产品」。列表默认按最近更新排序，每页 24 条，网址会记住你的筛选状态。"
        : "Click Products in the sidebar. The list defaults to newest-first with 24 rows per page; every filter is persisted in the URL.",
      hint: zh
        ? "外销角色默认已限定「seller = 自己」的产品；点击工具栏「显示全部」可以看到全部产品。"
        : "Export users are pre-scoped to seller = self; use 'Show All' in the toolbar to see everything.",
      mock: <MockToolbar lang={lang} highlight="products" />,
    },
    {
      id: "filter",
      icon: FilterIcon,
      route: "/products?type=all",
      cta: zh ? "去筛选" : "Try filtering",
      title: zh ? "使用筛选条件锁定目标产品" : "Narrow down with filters",
      desc: zh
        ? "顶部筛选栏支持：产品类型、类别、子类别、品牌、创建日期、工厂成本区间、销售价区间。快捷按钮：⭐ 我的收藏、🕒 最近查看、👤 我的产品。"
        : "Filter row: product type · category · subcategory · brand · created-at · factory cost · selling price. Quick buttons: ⭐ Favorites, 🕒 Recent, 👤 My Products.",
      hint: zh
        ? "多个条件可以组合，切换币种（US$/C$）会自动应用到价格区间和后续报价。"
        : "Filters combine. Switching currency (US$/C$) updates both price ranges and later quotes.",
      mock: <MockFilterRow lang={lang} />,
    },
    {
      id: "search",
      icon: SearchIcon,
      route: "/products",
      cta: zh ? "去搜索" : "Try search",
      title: zh ? "关键词搜索快速定位" : "Search by keyword",
      desc: zh
        ? "在搜索框输入货号、描述、材质或规格关键词，边打边搜，不用按回车。"
        : "Type item number, description, material or spec keywords in the search box. Debounced at 300 ms so it searches as you type.",
      hint: zh
        ? "空格可分隔多个关键词做 AND 匹配，例如「blanket king」会找到同时匹配两者的产品。"
        : "Space-separated tokens act as AND, e.g. 'blanket king' matches items containing both.",
      mock: <MockSearchBox lang={lang} />,
    },
    {
      id: "select",
      icon: CheckSquare,
      route: "/products",
      cta: zh ? "去勾选" : "Try selecting",
      title: zh ? "勾选想要报价的产品" : "Tick the products you want to quote",
      desc: zh
        ? "每行左侧的复选框逐个勾选，或点顶部「全选」勾选当前页全部。选中数量保留 24 小时，跨页保留，换设备也不丢。"
        : "Tick the checkbox on each row, or use 'Select All' at the top for the whole page. Selection persists 24 h, across pages, and per user across devices.",
      hint: zh
        ? "选择过多想清空？点顶部红色「清空选择」按钮一秒还原。"
        : "Overselected? Hit the red 'Clear selection' button at the top.",
      mock: <MockSelectionBar lang={lang} />,
    },
    {
      id: "add-to-quote",
      icon: ShoppingCart,
      route: "/products",
      cta: zh ? "去加入报价单" : "Add to Quote",
      title: zh ? "点「批量操作 → 加入报价单」" : "Click 'Bulk actions → Add to Quote'",
      desc: zh
        ? "顶部工具栏的「批量操作」下拉，选择「加入报价单」。如果你已经选了「当前活跃报价单」（右上角购物车），直接加入；否则弹出选择器让你挑一张或新建。"
        : "Open the 'Bulk actions' dropdown at the top and pick 'Add to Quote'. Items go straight into your active quote (cart icon in the header) — or a picker appears to choose / create one.",
      hint: zh
        ? "外销 / GT 外销 / 采购 / 销售主管 都能加，产品经理不可以（只做资料）。"
        : "Available to export / gt_export / buyer / sales_manager. Product managers can't (data-only role).",
      mock: <MockActionMenu lang={lang} />,
    },
    {
      id: "pick-quote",
      icon: FileText,
      route: "/quotes",
      cta: zh ? "查看报价单" : "See Quotes",
      title: zh ? "选择或新建报价单" : "Pick or create a quote",
      desc: zh
        ? "弹窗以方块形式展示最近报价单，可以点选进入；也能直接点「+ 新建报价单」，选择客户后立刻创建并加入。"
        : "The picker shows recent quotes as tiles. Click one to add — or hit '+ New quote', select the customer, and it's created + populated instantly.",
      hint: zh
        ? "报价单会记住客户的货币和报价系数——价格 = 工厂成本 × (1 + 关税) ÷ (1 - 退税率) × 汇率 × 客户系数。"
        : "Each quote carries the customer's currency + multiplier. Price = factory cost × (1 + duty) ÷ (1 − rebate) × FX × customer multiplier.",
      mock: <MockQuotePicker lang={lang} />,
    },
    {
      id: "edit-quote",
      icon: Edit3,
      route: "/quotes",
      cta: zh ? "去编辑报价单" : "Edit Quote",
      title: zh ? "在报价单里微调价格、备注和 MOQ" : "Fine-tune price, MOQ & notes in the quote",
      desc: zh
        ? "报价单页面直接内联编辑：MOQ、装箱数、包装、报价、目标价、备注。价格公式实时计算 —— 改工厂成本，客户价立刻联动。"
        : "Everything in the quote table is inline-editable: MOQ, pack qty, packaging, price, target price, remarks. The pricing formula recomputes live as you type.",
      hint: zh
        ? "点右上角「共享」把报价单共享给同事只读查看；销售主管默认能看全组报价单。"
        : "Share via the top-right button for read-only teammate access. Sales managers see the whole team's quotes.",
      mock: <MockQuoteRow lang={lang} />,
    },
    {
      id: "export",
      icon: Download,
      route: "/quotes",
      cta: zh ? "去导出" : "Export",
      title: zh ? "一键导出 Excel / PDF / 邮件" : "Export to Excel / PDF / Email",
      desc: zh
        ? "报价单顶部：Excel（带缩略图 + 公式）· PDF（含产品图 + 二维码）· 打印标签 · HTML 邮件（Resend 直发客户，抄送自己）。"
        : "Top of the quote: Excel (with thumbnails + formulas), PDF (photo grid + QR codes), Print labels, and HTML Email (sent via Resend to the customer + you).",
      hint: zh
        ? "邮件发送前会弹窗让你调整正文和收件人，模板从「系统模板」页维护，一处修改，全员生效。"
        : "The email dialog lets you tweak the body and recipients. Templates live under Settings → System Templates — edit once, applied everywhere.",
      mock: <MockExportButtons lang={lang} />,
    },
  ];
};

/* --------------------------------------------------------------------- */
/* Mock UI 组件 - 用纯 tailwind 画的简化界面示意图                              */
/* --------------------------------------------------------------------- */

function MockToolbar({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner text-[10px]">
      <div className="flex items-center gap-1.5">
        <div className="h-5 px-2 bg-brand text-white rounded flex items-center font-medium">
          <Package size={10} className="mr-1" /> {lang === "zh" ? "产品" : "Products"}
        </div>
        <div className="h-5 px-2 bg-muted rounded flex items-center text-muted-foreground">
          {lang === "zh" ? "报价单" : "Quotes"}
        </div>
        <div className="h-5 px-2 bg-muted rounded flex items-center text-muted-foreground">
          {lang === "zh" ? "客户" : "Customers"}
        </div>
      </div>
    </div>
  );
}

function MockFilterRow({ lang }) {
  const chips = lang === "zh"
    ? ["产品类型", "类别", "子类别", "工厂成本", "销售价"]
    : ["Type", "Category", "Subcategory", "Cost", "Price"];
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c, i) => (
          <div
            key={c}
            className={`h-6 px-2 rounded border flex items-center text-[10px] font-mono ${
              i === 0 ? "border-brand bg-brand/10 text-brand" : "border-border bg-muted/40 text-muted-foreground"
            }`}
          >
            {c} <ChevronDown size={10} className="ml-0.5" />
          </div>
        ))}
      </div>
    </div>
  );
}

function MockSearchBox({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="h-7 border border-brand rounded-md px-2 flex items-center gap-1.5 bg-brand/5">
        <SearchIcon size={12} className="text-brand" />
        <span className="text-[11px] font-mono text-foreground">blanket king queen</span>
        <span className="ml-auto animate-pulse text-[10px] text-brand">
          {lang === "zh" ? "搜索中…" : "searching…"}
        </span>
      </div>
    </div>
  );
}

function MockSelectionBar({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner space-y-1">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex items-center gap-2 py-0.5">
          <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
            n < 3 ? "bg-brand border-brand text-white" : "border-border bg-white"
          }`}>
            {n < 3 && <span className="text-[8px]">✓</span>}
          </div>
          <div className="w-6 h-6 rounded bg-muted" />
          <div className="text-[10px] font-mono text-muted-foreground">
            SKU-{1000 + n} · {lang === "zh" ? "全棉毛毯" : "Cotton Blanket"}
          </div>
        </div>
      ))}
      <div className="mt-1 pt-1 border-t border-border flex items-center gap-1.5 text-[10px]">
        <span className="font-mono text-brand font-semibold">2 {lang === "zh" ? "已选" : "selected"}</span>
        <span className="ml-auto h-5 px-2 rounded bg-brand text-white flex items-center font-medium">
          {lang === "zh" ? "批量操作 ▾" : "Bulk actions ▾"}
        </span>
      </div>
    </div>
  );
}

function MockActionMenu({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="w-44 border border-border rounded-md bg-white shadow-md p-1 text-[10px]">
        <div className="px-2 py-1 rounded bg-brand/10 text-brand font-semibold flex items-center gap-1.5">
          <ShoppingCart size={10} /> {lang === "zh" ? "加入报价单" : "Add to Quote"}
        </div>
        <div className="px-2 py-1 text-muted-foreground">
          {lang === "zh" ? "打印标签" : "Print labels"}
        </div>
        <div className="px-2 py-1 text-muted-foreground">
          {lang === "zh" ? "设置标签" : "Set tag"}
        </div>
        <div className="px-2 py-1 text-muted-foreground">
          {lang === "zh" ? "批量收藏" : "Bulk favorite"}
        </div>
        <div className="px-2 py-1 text-muted-foreground">
          {lang === "zh" ? "网格 PDF" : "Grid PDF"}
        </div>
      </div>
    </div>
  );
}

function MockQuotePicker({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="grid grid-cols-3 gap-1.5">
        <div className="aspect-square border-2 border-dashed border-brand rounded-md flex flex-col items-center justify-center text-brand text-[10px] font-semibold">
          <span className="text-lg leading-none">+</span>
          {lang === "zh" ? "新建" : "New"}
        </div>
        <div className="aspect-square border border-brand bg-brand/5 rounded-md p-1.5 flex flex-col justify-between">
          <div className="text-[9px] font-mono text-brand font-bold truncate">Q-2026-021</div>
          <div className="text-[9px] text-muted-foreground truncate">Costco · US$</div>
          <div className="text-[9px] text-brand font-semibold">12 {lang === "zh" ? "项" : "items"}</div>
        </div>
        <div className="aspect-square border border-border rounded-md p-1.5 flex flex-col justify-between">
          <div className="text-[9px] font-mono text-muted-foreground truncate">Q-2026-018</div>
          <div className="text-[9px] text-muted-foreground truncate">Walmart · C$</div>
          <div className="text-[9px] text-muted-foreground">6 {lang === "zh" ? "项" : "items"}</div>
        </div>
      </div>
    </div>
  );
}

function MockQuoteRow({ lang }) {
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 py-0.5">
        <div className="w-8 h-8 rounded bg-muted" />
        <div>
          <div className="text-[10px] font-mono font-semibold">SKU-1001</div>
          <div className="text-[9px] text-muted-foreground">100% Cotton Blanket · 60"x80"</div>
        </div>
        <div className="text-[10px] text-muted-foreground">MOQ 500</div>
        <div className="text-[11px] font-mono font-bold text-brand">$8.45</div>
      </div>
      <div className="text-[9px] text-muted-foreground mt-1 border-t border-border pt-1">
        {lang === "zh" ? "公式：成本 × (1 + 关税) ÷ (1 - 退税) × 汇率 × 系数" : "cost × (1+duty) ÷ (1−rebate) × FX × multiplier"}
      </div>
    </div>
  );
}

function MockExportButtons({ lang }) {
  const items = lang === "zh"
    ? [{ label: "Excel", color: "bg-emerald-500" }, { label: "PDF", color: "bg-rose-500" }, { label: "邮件", color: "bg-indigo-500" }, { label: "标签", color: "bg-amber-500" }]
    : [{ label: "Excel", color: "bg-emerald-500" }, { label: "PDF", color: "bg-rose-500" }, { label: "Email", color: "bg-indigo-500" }, { label: "Labels", color: "bg-amber-500" }];
  return (
    <div className="mt-3 bg-white border border-border rounded-md p-2 shadow-inner">
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <div key={it.label} className={`h-6 px-2 rounded ${it.color} text-white text-[10px] font-semibold flex items-center gap-1`}>
            <Download size={10} /> {it.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Main component                                                         */
/* --------------------------------------------------------------------- */
export default function QuoteWorkflowTutorial({ lang = "zh" }) {
  const zh = lang === "zh";
  const navigate = useNavigate();
  const steps = STEPS(lang);
  const [openIdx, setOpenIdx] = useState(0); // first step open by default

  return (
    <section
      className="rounded-md border-2 border-brand/30 bg-gradient-to-br from-brand/8 via-white to-brand/4 p-5"
      data-testid="quote-workflow-tutorial"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-brand text-white flex items-center justify-center shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-xl font-bold tracking-tight">
              {zh ? "教程：外销筛选产品 → 自动生成报价单" : "Tutorial: Filter Products → Auto-Generate a Quote"}
            </h2>
            <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-brand text-white rounded">
              {zh ? "教程" : "GUIDE"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-3xl">
            {zh
              ? "全流程 8 步，跟着做即可完成。每步都可以点右侧「去操作」直接跳到对应页面边看边学。"
              : "A guided 8-step walkthrough. Click 'Try it now' on any step to jump straight to the matching page and follow along."}
          </p>
        </div>
      </div>

      {/* Overview chip strip */}
      <div className="mb-5 overflow-x-auto pb-1">
        <div className="flex items-center gap-1.5 min-w-max" data-testid="tutorial-chip-strip">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === openIdx;
            return (
              <div key={s.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => setOpenIdx(i)}
                  className={`flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-all ${
                    isActive
                      ? "border-brand bg-brand text-white shadow-sm"
                      : "border-border bg-white text-muted-foreground hover:border-brand/60 hover:text-brand"
                  }`}
                  data-testid={`tutorial-chip-${s.id}`}
                >
                  <span className="font-mono text-[10px] opacity-70">{String(i + 1).padStart(2, "0")}</span>
                  <Icon size={12} />
                </button>
                {i < steps.length - 1 && (
                  <ArrowRight size={12} className="text-muted-foreground/40 mx-0.5 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Vertical timeline */}
      <div className="space-y-2">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const isOpen = i === openIdx;
          return (
            <div
              key={step.id}
              className={`border rounded-md transition-all ${
                isOpen ? "border-brand shadow-sm bg-white" : "border-border bg-white/60 hover:border-brand/40"
              }`}
              data-testid={`tutorial-step-${step.id}`}
            >
              {/* Header row (always visible, click to toggle) */}
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                className="w-full flex items-center gap-3 p-3 text-left"
                data-testid={`tutorial-step-toggle-${step.id}`}
              >
                <div
                  className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 font-mono text-xs font-bold ${
                    isOpen ? "bg-brand text-white" : "bg-brand/10 text-brand"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <Icon size={16} className={isOpen ? "text-brand" : "text-muted-foreground"} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{step.title}</div>
                  {!isOpen && (
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{step.desc}</div>
                  )}
                </div>
                <ChevronDown
                  size={16}
                  className={`text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Body (expanded content) */}
              {isOpen && (
                <div className="px-3 pb-4 pl-16 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                  <div>
                    <p className="text-sm leading-relaxed text-foreground/90">{step.desc}</p>
                    <div className="mt-2 flex items-start gap-1.5 text-xs bg-amber-50 border-l-2 border-amber-400 pl-2 py-1.5 rounded-r">
                      <span className="font-mono font-semibold text-amber-700 shrink-0">TIP</span>
                      <span className="text-amber-900 leading-relaxed">{step.hint}</span>
                    </div>
                    {step.mock}
                  </div>
                  <div className="flex flex-col gap-2 items-stretch md:items-end">
                    <button
                      type="button"
                      onClick={() => navigate(step.route)}
                      className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md bg-brand text-white text-xs font-semibold hover:bg-brand-hover transition-colors whitespace-nowrap"
                      data-testid={`tutorial-cta-${step.id}`}
                    >
                      {step.cta} <ArrowRight size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenIdx(Math.min(i + 1, steps.length - 1))}
                      disabled={i === steps.length - 1}
                      className="text-xs text-muted-foreground hover:text-brand disabled:opacity-40 disabled:hover:text-muted-foreground whitespace-nowrap"
                      data-testid={`tutorial-next-${step.id}`}
                    >
                      {zh ? "下一步 →" : "Next →"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer CTA */}
      <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">
          {zh
            ? "小提示：整个流程熟练后约 30 秒可以完成一张 10 项产品的报价单。"
            : "Tip: with practice this whole flow takes ~30 seconds for a 10-item quote."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/products")}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-brand text-white text-xs font-semibold hover:bg-brand-hover transition-colors"
          data-testid="tutorial-start-now"
        >
          {zh ? "现在就开始 →" : "Start now →"}
        </button>
      </div>
    </section>
  );
}
